import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, Send, Trash2, Upload } from 'lucide-react';
import {
  Badge,
  Button,
  Callout,
  EmptyState,
  ErrorState,
  Field,
  IconButton,
  Input,
  KeyValueList,
  NativeSelect,
  PageHeader,
  Section,
  Skeleton,
  StatusDot,
  Table,
  TableContainer,
  TableMessage,
  TableSkeleton,
  TBody,
  Td,
  Th,
  THead,
  Tr,
  cn,
  useToast,
} from '@zentera/ui';
import { api, ApiError, fmt, PROFILE_LABEL, TraceEvent, type EngineEvent } from '@zentera/web-core';
import { usePageTitle } from '../components/shell';
import { useCan } from '../lib/auth';

interface Status {
  config: { receivedAt: string; profiles: { key: string; name: string }[]; integration: { certified: boolean; enabledOperations: number } } | null;
  aiService: { active: boolean };
  knowledge: { version: number; chunkCount: number; semanticSearch: boolean; publishedAt: string } | null;
  pendingChanges: boolean;
}

/* ───────────── Assistente: estado e publicação ───────────── */

export function AssistantPage() {
  usePageTitle('Assistente');
  const can = useCan();
  const qc = useQueryClient();
  const toast = useToast();
  const [note, setNote] = useState('');
  const status = useQuery({ queryKey: ['assistant-status'], queryFn: () => api.get<Status>('/api/assistant/status') });
  const versions = useQuery({ queryKey: ['knowledge-versions'], queryFn: () => api.get<{ items: { id: string; version: number; status: string; chunkCount: number; semanticSearch: boolean; note: string | null; createdAt: string }[] }>('/api/knowledge/versions') });
  const publish = useMutation({
    mutationFn: () => api.post<{ version: { version: number; chunkCount: number }; semanticSearch: boolean }>('/api/knowledge/publish', { note: note || undefined }),
    onSuccess: (r) => {
      setNote('');
      void qc.invalidateQueries({ queryKey: ['assistant-status'] });
      void qc.invalidateQueries({ queryKey: ['knowledge-versions'] });
      toast({
        tone: 'success',
        title: `Versão ${r.version.version} publicada`,
        description: `${r.version.chunkCount} trechos disponíveis para o assistente.`,
      });
    },
    onError: (e) => toast({ tone: 'danger', title: 'Falha ao publicar', description: e instanceof ApiError ? e.message : undefined }),
  });

  if (status.isError) return <ErrorState error={status.error} onRetry={() => void status.refetch()} />;
  if (!status.data) return <Skeleton className="h-96 rounded-lg" />;
  const s = status.data;

  return (
    <div className="space-y-10">
      <PageHeader title="Assistente virtual" description="O comportamento do assistente é definido pela Zentera. Aqui você publica o conhecimento que ele usa e acompanha o que está ativo nesta instalação." />

      <Section
        title="Conhecimento publicado"
        description="O assistente só usa o conteúdo publicado. Edições em planos, políticas, FAQ, horários e documentos ficam pendentes até a próxima publicação."
      >
        {s.pendingChanges && (
          <Callout tone="warning" className="mb-4" title="Há alterações não publicadas">
            O assistente ainda responde com a versão anterior.
          </Callout>
        )}
        <KeyValueList
          items={[
            { label: 'Versão em uso', value: s.knowledge ? `v${s.knowledge.version} · ${fmt.dateTime(s.knowledge.publishedAt)}` : <span className="text-fg-4">Nenhuma publicada</span> },
            { label: 'Trechos', value: s.knowledge ? <span className="tabular">{fmt.int(s.knowledge.chunkCount)}</span> : '—' },
            {
              label: 'Busca',
              value: s.knowledge?.semanticSearch ? 'Por significado e palavras-chave' : 'Por palavras-chave',
            },
          ]}
        />
        {can('knowledge.write') && (
          <div className="mt-4 flex max-w-xl items-end gap-2">
            <Field label="Nota da publicação" optional className="flex-1">
              <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ex.: novos planos de novembro" />
            </Field>
            <Button variant="primary" loading={publish.isPending} onClick={() => publish.mutate()}>
              Publicar conhecimento
            </Button>
          </div>
        )}
      </Section>

      <Section title="Histórico de publicações">
        <TableContainer>
          <Table>
            <THead><tr><Th>Versão</Th><Th>Situação</Th><Th align="right">Trechos</Th><Th>Busca</Th><Th>Nota</Th><Th>Publicada</Th></tr></THead>
            <TBody>
              {versions.isPending ? <TableSkeleton columns={6} rows={2} /> : (versions.data?.items ?? []).length === 0 ? (
                <TableMessage columns={6}><EmptyState compact title="Nenhuma publicação ainda" /></TableMessage>
              ) : (
                versions.data!.items.map((v) => (
                  <Tr key={v.id}>
                    <Td className="font-mono text-xs">v{v.version}</Td>
                    <Td>{v.status === 'published' ? <Badge tone="success">Em uso</Badge> : <Badge tone="muted">Arquivada</Badge>}</Td>
                    <Td align="right">{v.chunkCount}</Td>
                    <Td className="text-xs text-fg-3">{v.semanticSearch ? 'significado + palavras' : 'palavras-chave'}</Td>
                    <Td className="max-w-xs truncate text-fg-2">{v.note ?? '—'}</Td>
                    <Td>{fmt.dateTime(v.createdAt)}</Td>
                  </Tr>
                ))
              )}
            </TBody>
          </Table>
        </TableContainer>
      </Section>

      <div className="grid gap-x-12 gap-y-8 lg:grid-cols-2">
        <Section title="Configuração recebida da Zentera">
          {s.config ? (
            <KeyValueList
              items={[
                { label: 'Recebida', value: fmt.dateTime(s.config.receivedAt) },
                { label: 'Perfis ativos', value: s.config.profiles.map((p) => p.name).join(', ') || '—' },
                { label: 'Integração com o sistema de gestão', value: s.config.integration.certified ? `${s.config.integration.enabledOperations} consultas liberadas` : 'Não ativada — o assistente não consulta dados de clientes' },
              ]}
            />
          ) : (
            <p className="text-sm text-fg-3">Aguardando a primeira configuração da Zentera.</p>
          )}
        </Section>
        <Section title="Serviço de IA">
          <KeyValueList
            items={[
              {
                label: 'Situação',
                value: s.aiService.active ? <StatusDot tone="success">Ativo</StatusDot> : <StatusDot tone="muted">Aguardando ativação pela Zentera</StatusDot>,
              },
            ]}
          />
          <p className="mt-3 text-sm text-fg-3">O serviço de IA é operado e monitorado pela Zentera, com redundância automática.</p>
        </Section>
      </div>
    </div>
  );
}

/* ───────────── Documentos ───────────── */

interface Doc { id: string; name: string; mime: string; sizeBytes: number; pages: number | null; chars: number; createdAt: string }

export function DocumentsPage() {
  usePageTitle('Documentos');
  const can = useCan();
  const qc = useQueryClient();
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const q = useQuery({ queryKey: ['documents'], queryFn: () => api.get<{ items: Doc[] }>('/api/knowledge/documents') });
  const upload = useMutation({
    mutationFn: async (file: File) => {
      if (file.size > 5 * 1024 * 1024) throw new Error('Arquivo maior que 5 MB');
      const mime = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : file.name.toLowerCase().endsWith('.md') ? 'text/markdown' : 'text/plain';
      const buf = new Uint8Array(await file.arrayBuffer());
      let bin = '';
      for (let i = 0; i < buf.length; i += 0x8000) bin += String.fromCharCode(...buf.subarray(i, i + 0x8000));
      return api.post<{ pages: number | null; chars: number }>('/api/knowledge/documents', { name: file.name, mime, contentBase64: btoa(bin) });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['assistant-status'] });
      toast({ tone: 'success', title: 'Documento adicionado', description: `${fmt.int(r.chars)} caracteres extraídos. Publique o conhecimento para o assistente usar.` });
    },
    onError: (e) => toast({ tone: 'danger', title: 'Não foi possível adicionar', description: (e as Error).message }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.del(`/api/knowledge/documents/${id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['documents'] });
      void qc.invalidateQueries({ queryKey: ['assistant-status'] });
    },
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title="Documentos"
        description="Contratos-modelo, regulamentos e manuais em PDF ou texto. O conteúdo é extraído e indexado somente nesta instalação."
        actions={
          can('knowledge.write') && (
            <>
              <input ref={input} type="file" accept=".pdf,.txt,.md,application/pdf,text/plain" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) upload.mutate(f); e.target.value = ''; }} />
              <Button variant="primary" icon={<Upload />} loading={upload.isPending} onClick={() => input.current?.click()}>Adicionar documento</Button>
            </>
          )
        }
      />
      <TableContainer>
        <Table>
          <THead><tr><Th>Documento</Th><Th align="right">Tamanho</Th><Th align="right">Páginas</Th><Th align="right">Texto extraído</Th><Th>Adicionado</Th><Th className="w-10"><span className="sr-only">Ações</span></Th></tr></THead>
          <TBody>
            {q.isPending ? <TableSkeleton columns={6} rows={3} /> : q.isError ? (
              <TableMessage columns={6}><ErrorState error={q.error} /></TableMessage>
            ) : q.data.items.length === 0 ? (
              <TableMessage columns={6}><EmptyState icon={<FileText />} title="Nenhum documento" description="PDFs digitalizados sem texto (imagem) não podem ser lidos; envie a versão com texto selecionável. Limite de 5 MB." /></TableMessage>
            ) : (
              q.data.items.map((d) => (
                <Tr key={d.id}>
                  <Td className="font-medium">{d.name}</Td>
                  <Td align="right">{(d.sizeBytes / 1024).toFixed(0)} KB</Td>
                  <Td align="right">{d.pages ?? '—'}</Td>
                  <Td align="right">{fmt.int(d.chars)} caract.</Td>
                  <Td className="text-fg-2">{fmt.relative(d.createdAt)}</Td>
                  <Td>{can('knowledge.write') && <IconButton size="sm" label={`Remover ${d.name}`} icon={<Trash2 />} onClick={() => remove.mutate(d.id)} />}</Td>
                </Tr>
              ))
            )}
          </TBody>
        </Table>
      </TableContainer>
    </div>
  );
}

/* ───────────── Testar assistente ───────────── */

interface Turn { id: number; customer: string; reply: string | null; events: EngineEvent[] }

export function TestAssistantPage() {
  usePageTitle('Testar assistente');
  const [persona, setPersona] = useState<'maria' | 'joao' | 'desconhecido'>('maria');
  const [mode, setMode] = useState<'ai' | 'simulation'>('simulation');
  const [conv, setConv] = useState<{ id: string; persona: string; mode: string } | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [state, setState] = useState<{ status: string; activeProfile: string; identified: boolean } | null>(null);
  const [text, setText] = useState('');
  const [selected, setSelected] = useState<number | null>(null);

  const start = useMutation({
    mutationFn: () => api.post<{ id: string; persona: string; mode: string }>('/api/assistant/test-conversations', { persona, mode }),
    onSuccess: (c) => {
      setConv(c);
      setTurns([]);
      setState(null);
      setSelected(null);
    },
  });
  const send = useMutation({
    mutationFn: (msg: string) => api.post<{ reply: string | null; events: EngineEvent[]; state: { status: string; activeProfile: string; identified: boolean } }>(`/api/assistant/test-conversations/${conv!.id}/messages`, { text: msg }),
    onSuccess: (r, msg) => {
      const id = Date.now();
      setTurns((t) => [...t, { id, customer: msg, reply: r.reply, events: r.events }]);
      setState(r.state);
      setSelected(id);
    },
  });
  const current = turns.find((t) => t.id === selected) ?? turns.at(-1);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Testar assistente"
        description="Converse com o assistente desta instalação usando o conhecimento publicado. Os dados de clientes são fictícios (sistema de gestão simulado) — nenhum cliente real é consultado."
      />
      <div className="flex flex-wrap items-end gap-3 border-b border-line pb-5">
        <Field label="Simular como">
          <NativeSelect value={persona} onChange={(e) => setPersona(e.target.value as typeof persona)} className="w-72">
            <option value="maria">Maria · cliente fictícia (CPF 529.982.247-25)</option>
            <option value="joao">João · cliente fictício (CPF 111.444.777-35)</option>
            <option value="desconhecido">Número sem cadastro</option>
          </NativeSelect>
        </Field>
        <Field label="Modelo">
          <NativeSelect value={mode} onChange={(e) => setMode(e.target.value as typeof mode)} className="w-64">
            <option value="simulation">Simulação por regras (sem IA)</option>
            <option value="ai">IA configurada pela Zentera</option>
          </NativeSelect>
        </Field>
        <Button variant="primary" loading={start.isPending} onClick={() => start.mutate()}>{conv ? 'Nova conversa' : 'Iniciar conversa'}</Button>
        {conv && <span className="text-xs text-fg-3">{conv.persona} · {conv.mode === 'simulation' ? 'simulação por regras' : 'IA'}</span>}
      </div>

      {!conv ? (
        <EmptyState title="Escolha o cliente fictício e o modelo" description="O modo de simulação não usa IA: serve para verificar o conhecimento publicado e as regras de segurança. Use o modo IA para avaliar as respostas reais." />
      ) : (
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_380px]">
          <div className="flex min-h-[460px] flex-col rounded-lg border border-line">
            <div className="flex items-center justify-between border-b border-line px-4 py-2.5 text-xs text-fg-3">
              <span>Conversa de teste</span>
              {state && (
                <span className="flex items-center gap-3">
                  <span>Setor: <span className="text-fg">{PROFILE_LABEL[state.activeProfile] ?? state.activeProfile}</span></span>
                  <span>{state.identified ? 'Cliente identificado' : 'Não identificado'}</span>
                  {state.status === 'human' && <Badge tone="warning">Transferido para humano</Badge>}
                </span>
              )}
            </div>
            <ol className="flex-1 space-y-4 overflow-y-auto px-4 py-4">
              {turns.length === 0 && <li className="py-10 text-center text-sm text-fg-4">Envie uma mensagem como se fosse o cliente.</li>}
              {turns.map((t) => (
                <li key={t.id} className="space-y-2">
                  <div className="ml-auto w-fit max-w-[80%] rounded-lg bg-muted px-3 py-2 text-sm whitespace-pre-wrap">{t.customer}</div>
                  {t.reply ? (
                    <button
                      onClick={() => setSelected(t.id)}
                      className={cn('block w-fit max-w-[85%] rounded-lg border px-3 py-2 text-left text-sm whitespace-pre-wrap transition-colors', current?.id === t.id ? 'border-brand-line bg-brand-subtle/40' : 'border-line hover:bg-subtle')}
                    >
                      {t.reply}
                    </button>
                  ) : (
                    <p className="text-xs text-fg-4">Sem resposta automática (atendimento humano).</p>
                  )}
                </li>
              ))}
              {send.isPending && <li className="text-xs text-fg-4">Processando…</li>}
            </ol>
            {send.error && <p role="alert" className="px-4 pb-2 text-sm text-danger">{send.error instanceof ApiError ? send.error.message : 'Falha ao enviar'}</p>}
            <form
              className="flex gap-2 border-t border-line p-3"
              onSubmit={(e) => {
                e.preventDefault();
                if (text.trim()) {
                  send.mutate(text.trim());
                  setText('');
                }
              }}
            >
              <Input aria-label="Mensagem do cliente" value={text} onChange={(e) => setText(e.target.value)} placeholder="Ex.: quero a segunda via da minha fatura" />
              <IconButton type="submit" variant="primary" label="Enviar" icon={<Send />} disabled={send.isPending || !text.trim()} />
            </form>
          </div>

          <aside className="rounded-lg border border-line" aria-label="Rastro do motor">
            <div className="border-b border-line px-4 py-2.5 text-xs font-medium text-fg-2">Rastro da resposta</div>
            {!current ? (
              <p className="px-4 py-6 text-sm text-fg-4">Cada resposta mostra o conhecimento consultado, as chamadas ao modelo e as decisões do Gateway de Segurança.</p>
            ) : (
              <ol className="divide-y divide-line">
                {current.events.map((e, i) => (
                  <li key={i} className="px-4 py-2.5 text-xs">
                    <TraceEvent e={e} />
                  </li>
                ))}
              </ol>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

